import styled from "@emotion/styled";
import AuthIllustrationImage from "@/assets/images/auth-illustration.svg?react";
import { MDSBreakpoints } from "@/mds/foundations";
import { APP_CONFIG } from "@/domains/global/config";
import { MDSIcon, MDSIconKind, MDSLogo, MDSLogoKind } from "@/mds";
import { MDSLink } from "@/mds/components/text";
import { windowModule } from "@/modules/window";

export const AuthLayout = ({ children }: { children: React.ReactNode }) => {
  const handleLearnMoreClick = () => {
    windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.LANDING_PAGE) });
  };

  return (
    <StyledContainer>
      <StyledContainerLeft>
        <StyledContentWrapper>{children}</StyledContentWrapper>
        <StyledLearnMoreLink onClick={handleLearnMoreClick}>
          Learn more
          <MDSIcon kind={MDSIconKind.ExternalLink} size={12} />
        </StyledLearnMoreLink>
      </StyledContainerLeft>
      <StyledContainerRight>
        <AuthIllustrationImage className="auth-illustration-image" />
      </StyledContainerRight>
      <MDSLogo kind={MDSLogoKind.Lockup} className="auth-wordmark" />
    </StyledContainer>
  );
};

const StyledContentWrapper = styled.div`
  display: flex;
  width: 100%;

  @media (${MDSBreakpoints.desktop}) {
    flex: 1;
    align-items: center;
  }
`;

const StyledLearnMoreLink = styled(MDSLink)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.fontSizes["text-sm"]};
  -webkit-app-region: no-drag;
  position: absolute;
  right: ${({ theme }) => theme.spacing[6]};
  bottom: ${({ theme }) => theme.spacing[5]};

  &:hover {
    color: ${({ theme }) => theme.colors.text.contrast};
  }

  @media (${MDSBreakpoints.desktop}) {
    left: 48px;
    right: auto;
    bottom: ${({ theme }) => theme.spacing[6]};
  }
`;

const StyledContainer = styled.div`
  position: relative;
  height: 100vh;
  width: 100vw;
  display: grid;
  grid-template-areas:
    "auth-illustration"
    "auth-content";
  grid-template-columns: 1fr;
  grid-template-rows: 30vh 1fr;
  -webkit-app-region: drag;

  .auth-wordmark {
    position: absolute;
    height: 18px;
    width: auto;
    left: ${({ theme }) => theme.spacing[5]};
    bottom: ${({ theme }) => theme.spacing[5]};
  }

  @media (${MDSBreakpoints.desktop}) {
    grid-template-areas: "auth-content auth-illustration";
    grid-template-columns: max(450px, 33%) 1fr;
    grid-template-rows: 1fr;

    .auth-wordmark {
      height: 32px;
      left: unset;
      right: ${({ theme }) => theme.spacing[5]};
      bottom: ${({ theme }) => theme.spacing[5]};
    }
  }
`;

const StyledContainerLeft = styled.div`
  grid-area: auth-content;
  background-color: ${({ theme }) => theme.colors.surface.pane};
  display: flex;
  flex-direction: column;
  padding-top: 40px;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;

  @media (${MDSBreakpoints.desktop}) {
    padding-top: 0;
  }
`;

const StyledContainerRight = styled.div`
  grid-area: auth-illustration;
  position: relative;
  background-color: ${({ theme }) => theme.colors.surface.onboarding};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[4]};
  overflow: hidden;

  svg.auth-illustration-image {
    position: absolute;
    color: ${({ theme }) => theme.colors.background.illustration.contrast};
    width: 105vw;
    height: auto;
    transform: rotate(5deg);
  }

  @media (${MDSBreakpoints.desktop}) {
    svg.auth-illustration-image {
      position: absolute;
      top: 16px;
      left: 16px;
      color: ${({ theme }) => theme.colors.background.illustration.contrast};
      width: calc(105vw - max(450px, 33vw));
      height: 100%;
      transform: rotate(5deg);
    }
  }
`;
